/**
 * Iter 99 — clean mobile payment summary per booking (spec §16):
 * totals, deposit, refunds, record-payment, and a Stripe payment link the
 * customer can open from their phone to pay the remaining balance.
 */
import { useCallback, useEffect, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Money from "@/components/Money";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Row } from "@/components/ops/wizardShared";
import { Copy, Link2, RefreshCw, Loader2, CheckCircle2, MessageCircle, ShieldCheck, FileDown, Mail } from "lucide-react";

const METHODS = ["Cash", "Card", "Bank transfer", "Paysera", "Stripe", "POS", "Other"];
const PAY_TYPES = [
  ["rental_balance", "Rental balance"], ["partial_payment", "Partial payment"],
  ["security_deposit", "Security deposit"], ["extra_charge", "Extra charge"],
  ["fuel_charge", "Fuel"], ["damage_charge", "Damage"], ["late_return_charge", "Late return"],
  ["cleaning_charge", "Cleaning"], ["refund", "Refund"], ["other", "Other"],
];

export default function PaymentsSheet({ booking, onClose }) {
  const { i18n } = useTranslation();
  const receiptLang = (i18n?.language || "en").startsWith("sq") ? "sq" : "en";
  const open = !!booking;
  const [data, setData] = useState(null);
  const [method, setMethod] = useState("Cash");
  const [amount, setAmount] = useState("");
  const [payType, setPayType] = useState("rental_balance");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [busy, setBusy] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);

  const load = useCallback(() => {
    if (!booking) return;
    api.get(`/bookings/${booking.id}/payments/summary`).then((r) => {
      setData(r.data);
      setAmount(r.data.amount_due_at_pickup > 0 ? String(r.data.amount_due_at_pickup) : "");
    }).catch((e) => toast.error(formatApiError(e)));
  }, [booking]);

  useEffect(() => { if (open) { setData(null); load(); } }, [open, load]);

  // Iter 99b/100b — auto-poll while a balance link or deposit hold is pending.
  useEffect(() => {
    const pendingBalance = data?.balance_payment?.status === "pending";
    const pendingDeposit = data?.deposit_hold?.status === "pending";
    if (!open || !booking || (!pendingBalance && !pendingDeposit)) return undefined;
    const t = setInterval(() => {
      if (pendingBalance) {
        api.post(`/bookings/${booking.id}/payment-link/check`).then((r) => {
          if (r.data.status === "paid") { toast.success("Balance paid ✅"); load(); }
        }).catch(() => {});
      }
      if (pendingDeposit) {
        api.post(`/bookings/${booking.id}/payment-link/check?purpose=deposit`).then((r) => {
          if (r.data.status === "held") { toast.success("Deposit held ✅"); load(); }
        }).catch(() => {});
      }
    }, 15000);
    return () => clearInterval(t);
  }, [open, booking, data?.balance_payment?.status, data?.deposit_hold?.status, load]);

  if (!open) return null;

  const record = async () => {
    if (!(Number(amount) > 0)) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/payments/record-v2`, {
        method, amount: Number(amount), payment_type: payType, reference: reference || null,
        note: note || null, notify_customer: notifyCustomer,
      });
      toast.success(notifyCustomer ? "Payment recorded — customer notified by email & WhatsApp" : "Payment recorded");
      setReference("");
      setNote("");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const editPayment = async (p) => {
    const v = window.prompt(`New amount for ${p.method} payment (was €${Math.abs(p.amount)})`, String(Math.abs(p.amount)));
    if (v == null) return;
    const reason = window.prompt("Reason for the correction (required)");
    if (!reason) { toast.error("A reason is required"); return; }
    try {
      await api.put(`/bookings/${booking.id}/payments/${p.id}`, { amount: Number(v), reason });
      toast.success("Payment corrected — audit kept");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const voidPayment = async (p) => {
    const reason = window.prompt("Reason for voiding this payment (required)");
    if (!reason) return;
    try {
      await api.post(`/bookings/${booking.id}/payments/${p.id}/void`, { reason });
      toast.success("Payment voided");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadReceipt = async (p) => {
    try {
      const res = await fetch(`${API}/bookings/${booking.id}/payments/${p.id}/receipt.pdf?lang=${receiptLang}`, { credentials: "include" });
      if (!res.ok) throw new Error("Receipt failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `receipt-${p.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate the receipt PDF"); }
  };

  const emailReceipt = async (p) => {
    try {
      const { data: r } = await api.post(`/bookings/${booking.id}/payments/${p.id}/receipt/email?lang=${receiptLang}`);
      toast.success(`Receipt emailed to ${r.sent_to}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendLink = async () => {
    setLinkBusy(true);
    try {
      const { data: r } = await api.post(`/bookings/${booking.id}/payment-link`, {});
      toast.success("Payment link created — it now shows on the customer's reservation");
      load();
      if (navigator.clipboard) navigator.clipboard.writeText(r.url).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); } finally { setLinkBusy(false); }
  };

  const checkLink = async () => {
    setLinkBusy(true);
    try {
      const { data: r } = await api.post(`/bookings/${booking.id}/payment-link/check`);
      toast[r.status === "paid" ? "success" : "info"](r.status === "paid" ? "Balance paid ✅" : "Not paid yet");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setLinkBusy(false); }
  };

  const bp = data?.balance_payment;
  const dh = data?.deposit_hold;
  const custWa = String(booking.customer?.whatsapp || booking.customer?.phone || "").replace(/[^\d]/g, "");

  const waShare = (url, kind) => {
    const text = encodeURIComponent(
      kind === "deposit"
        ? `Hi! Please pre-authorize your security deposit for reservation #${booking.id.slice(0, 8).toUpperCase()} here: ${url}`
        : `Hi! You can pay the remaining balance for reservation #${booking.id.slice(0, 8).toUpperCase()} securely by card here: ${url}`,
    );
    window.open(custWa ? `https://wa.me/${custWa}?text=${text}` : `https://wa.me/?text=${text}`, "_blank");
  };

  const depositLink = async () => {
    setLinkBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/payment-link`, { purpose: "deposit" });
      toast.success("Deposit hold link created");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setLinkBusy(false); }
  };

  const checkDeposit = async () => {
    setLinkBusy(true);
    try {
      const { data: r } = await api.post(`/bookings/${booking.id}/payment-link/check?purpose=deposit`);
      toast[r.status === "held" ? "success" : "info"](r.status === "held" ? "Deposit held ✅" : "Not authorized yet");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setLinkBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="bottom" className="rounded-t-3xl max-h-[88vh] overflow-y-auto" data-testid="payments-sheet">
        <SheetHeader className="mb-2">
          <SheetTitle className="text-left">Payments — #{(booking.id || "").slice(0, 8).toUpperCase()}</SheetTitle>
        </SheetHeader>
        {!data ? (
          <div className="text-center text-neutral-400 py-10">Loading…</div>
        ) : (
          <div className="space-y-4 pb-4">
            <div>
              <Row label="Rental total" value={<Money amount={data.amount} />} strong />
              <Row label="Paid online" value={<Money amount={data.amount_paid_online} />} />
              {data.return_charges_total > 0 && <Row label="Extra charges (return)" value={<Money amount={data.return_charges_total} />} />}
              <Row label="Remaining balance" value={<span data-testid="payments-remaining"><Money amount={data.amount_due_at_pickup} /></span>} strong />
              <Row label="Deposit" value={<Money amount={data.deposit_amount} />} />
              {data.deposit_status && <Row label="Deposit status" value={data.deposit_status} />}
              {data.payment_status === "refunded" && <Row label="Refund" value="Payment refunded" />}
            </div>

            {(data.manual_payments || []).length > 0 && (
              <div className="rounded-2xl border border-border p-3">
                <div className="text-xs font-bold text-neutral-500 uppercase tracking-wide mb-1.5">Payment history</div>
                {data.manual_payments.map((p) => (
                  <div key={p.id} className={`py-1.5 border-b border-dashed border-border last:border-0 ${p.status === "voided" ? "opacity-50" : ""}`} data-testid={`payment-history-${p.id}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className={`text-neutral-600 ${p.status === "voided" ? "line-through" : ""}`}>
                        {p.method}{p.payment_type ? ` · ${p.payment_type.replace(/_/g, " ")}` : ""}{p.reference ? ` · ${p.reference}` : ""}
                      </span>
                      <span className={`font-bold ${p.amount < 0 ? "text-rose-600" : ""} ${p.status === "voided" ? "line-through" : ""}`}><Money amount={p.amount} /></span>
                    </div>
                    {p.note && <div className="text-[11px] text-neutral-500 italic">“{p.note}”</div>}
                    {(p.notified?.email || p.notified?.whatsapp) && (
                      <div className="flex items-center gap-2 text-[10px] text-emerald-600 font-semibold" data-testid={`payment-notified-${p.id}`}>
                        {p.notified.email && <span className="inline-flex items-center gap-0.5"><Mail className="h-2.5 w-2.5" /> Emailed</span>}
                        {p.notified.whatsapp && <span className="inline-flex items-center gap-0.5"><MessageCircle className="h-2.5 w-2.5" /> WhatsApp sent</span>}
                      </div>
                    )}
                    <div className="flex items-center justify-between text-[11px] text-neutral-400">
                      <span>
                        {p.collected_by_name ? `Collected by ${p.collected_by_name} · ` : ""}{String(p.recorded_at || "").slice(0, 16).replace("T", " ")}
                        {p.status === "voided" && ` · VOIDED: ${p.void_reason}`}
                        {(p.edits || []).length > 0 && ` · ${p.edits.length} correction${p.edits.length > 1 ? "s" : ""}`}
                      </span>
                      {p.status !== "voided" && (
                        <span className="flex items-center gap-2 shrink-0">
                          <button type="button" onClick={() => downloadReceipt(p)} className="font-bold text-neutral-600 inline-flex items-center gap-0.5" data-testid={`receipt-payment-${p.id}`} title="Download PDF receipt"><FileDown className="h-3 w-3" /> PDF</button>
                          <button type="button" onClick={() => emailReceipt(p)} className="font-bold text-neutral-600 inline-flex items-center gap-0.5" data-testid={`email-receipt-${p.id}`} title="Email receipt to customer"><Mail className="h-3 w-3" /> Email</button>
                          <button type="button" onClick={() => editPayment(p)} className="font-bold text-blue-600" data-testid={`edit-payment-${p.id}`}>Edit</button>
                          <button type="button" onClick={() => voidPayment(p)} className="font-bold text-rose-500" data-testid={`void-payment-${p.id}`}>Void</button>
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {data.amount_due_at_pickup >= 0 && (
              <div className="rounded-2xl border border-border p-3 space-y-2.5">
                <div className="text-sm font-bold">Record payment</div>
                <div className="grid grid-cols-2 gap-1.5">
                  <select value={payType} onChange={(e) => setPayType(e.target.value)} className="rounded-xl border border-border px-2 py-2 text-sm" data-testid="record-type">
                    {PAY_TYPES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                  </select>
                  <select value={method} onChange={(e) => setMethod(e.target.value)} className="rounded-xl border border-border px-2 py-2 text-sm" data-testid="record-method">
                    {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Reference / receipt no. (optional)" className="rounded-xl h-10" data-testid="record-reference" />
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="rounded-xl h-10" data-testid="record-note" />
                <label className="flex items-center gap-2 text-xs text-neutral-600 cursor-pointer select-none">
                  <input type="checkbox" checked={notifyCustomer} onChange={(e) => setNotifyCustomer(e.target.checked)} className="h-4 w-4 rounded accent-emerald-600" data-testid="record-notify-toggle" />
                  Auto-send email & WhatsApp confirmation to the customer
                </label>
                <div className="flex gap-2">
                  <Input type="number" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="€" className="rounded-xl h-11" data-testid="record-amount" />
                  <button type="button" onClick={record} disabled={busy} className="shrink-0 rounded-xl bg-emerald-600 text-white text-sm font-bold px-5 disabled:opacity-60" data-testid="record-payment-btn">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Record"}
                  </button>
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-border p-3 space-y-2.5">
              <div className="text-sm font-bold flex items-center gap-1.5"><Link2 className="h-4 w-4" /> Stripe payment link</div>
              {bp ? (
                <>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-neutral-500">Link for <Money amount={bp.amount} /></span>
                    {bp.status === "paid"
                      ? <span className="inline-flex items-center gap-1 text-emerald-600 font-bold text-sm" data-testid="balance-link-paid"><CheckCircle2 className="h-4 w-4" /> Paid</span>
                      : <span className="text-amber-600 font-bold text-sm" data-testid="balance-link-pending">Pending</span>}
                  </div>
                  {bp.status !== "paid" && (
                    <div className="grid grid-cols-3 gap-2">
                      <button type="button" onClick={() => { navigator.clipboard?.writeText(bp.url); toast.success("Link copied"); }} className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border py-2.5 text-sm font-bold" data-testid="copy-balance-link">
                        <Copy className="h-4 w-4" /> Copy
                      </button>
                      <button type="button" onClick={() => waShare(bp.url, "balance")} className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 py-2.5 text-sm font-bold" data-testid="whatsapp-balance-link">
                        <MessageCircle className="h-4 w-4" /> WhatsApp
                      </button>
                      <button type="button" onClick={checkLink} disabled={linkBusy} className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border py-2.5 text-sm font-bold disabled:opacity-60" data-testid="check-balance-link">
                        <RefreshCw className="h-4 w-4" /> Status
                      </button>
                    </div>
                  )}
                  <div className="text-xs text-neutral-500">The customer also sees a "Pay remaining balance" button on their reservation page.</div>
                </>
              ) : data.amount_due_at_pickup > 0 ? (
                <button type="button" onClick={sendLink} disabled={linkBusy} className="w-full rounded-xl bg-blue-600 text-white text-sm font-bold py-3 disabled:opacity-60" data-testid="send-balance-link">
                  {linkBusy ? "Creating…" : <>Send payment link for <Money amount={data.amount_due_at_pickup} /></>}
                </button>
              ) : (
                <div className="text-sm text-neutral-500">Nothing left to pay — no link needed.</div>
              )}
            </div>

            {/* Iter 100 — deposit pre-authorization (card hold) via the same link flow. */}
            {data.deposit_amount > 0 && (
              <div className="rounded-2xl border border-border p-3 space-y-2.5">
                <div className="text-sm font-bold flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> Deposit hold (pre-authorization)</div>
                {dh ? (
                  <>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-neutral-500">Hold for <Money amount={dh.amount} /></span>
                      {dh.status === "held"
                        ? <span className="inline-flex items-center gap-1 text-emerald-600 font-bold text-sm" data-testid="deposit-hold-held"><CheckCircle2 className="h-4 w-4" /> Held</span>
                        : <span className={`font-bold text-sm capitalize ${dh.status === "pending" ? "text-amber-600" : "text-neutral-500"}`} data-testid="deposit-hold-pending">{dh.status || "Pending"}</span>}
                    </div>
                    {dh.status !== "held" && dh.status !== "released" && dh.status !== "captured" && (
                      <div className="grid grid-cols-3 gap-2">
                        <button type="button" onClick={() => { navigator.clipboard?.writeText(dh.url); toast.success("Link copied"); }} className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border py-2.5 text-sm font-bold" data-testid="copy-deposit-link">
                          <Copy className="h-4 w-4" /> Copy
                        </button>
                        <button type="button" onClick={() => waShare(dh.url, "deposit")} className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 py-2.5 text-sm font-bold" data-testid="whatsapp-deposit-link">
                          <MessageCircle className="h-4 w-4" /> WhatsApp
                        </button>
                        <button type="button" onClick={checkDeposit} disabled={linkBusy} className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border py-2.5 text-sm font-bold disabled:opacity-60" data-testid="check-deposit-link">
                          <RefreshCw className="h-4 w-4" /> Status
                        </button>
                      </div>
                    )}
                    {/* Iter 101 — release or capture the held pre-authorization. */}
                    {dh.status === "held" && (
                      <div className="grid grid-cols-2 gap-2">
                        <button type="button" onClick={async () => {
                          if (!window.confirm("Release the deposit hold? Funds return to the customer's card.")) return;
                          setLinkBusy(true);
                          try { await api.post(`/bookings/${booking.id}/deposit-hold/release`); toast.success("Deposit released"); load(); }
                          catch (e) { toast.error(formatApiError(e)); } finally { setLinkBusy(false); }
                        }} disabled={linkBusy} className="inline-flex items-center justify-center rounded-xl bg-emerald-600 text-white py-2.5 text-sm font-bold disabled:opacity-60" data-testid="release-deposit-btn">
                          Release deposit
                        </button>
                        <button type="button" onClick={async () => {
                          const v = window.prompt(`Amount to capture (max €${dh.amount})`, String(dh.amount));
                          if (v == null) return;
                          setLinkBusy(true);
                          try { const { data: r } = await api.post(`/bookings/${booking.id}/deposit-hold/capture`, { amount: Number(v) }); toast.success(`Captured €${r.amount}`); load(); }
                          catch (e) { toast.error(formatApiError(e)); } finally { setLinkBusy(false); }
                        }} disabled={linkBusy} className="inline-flex items-center justify-center rounded-xl bg-rose-600 text-white py-2.5 text-sm font-bold disabled:opacity-60" data-testid="capture-deposit-btn">
                          Capture deposit
                        </button>
                      </div>
                    )}
                    {dh.status === "released" && <div className="text-sm font-bold text-emerald-600" data-testid="deposit-released-state">✓ Deposit released to customer</div>}
                    {dh.status === "captured" && <div className="text-sm font-bold text-rose-600" data-testid="deposit-captured-state">Deposit captured</div>}
                  </>
                ) : (
                  <button type="button" onClick={depositLink} disabled={linkBusy} className="w-full rounded-xl bg-neutral-900 text-white text-sm font-bold py-3 disabled:opacity-60" data-testid="send-deposit-link">
                    {linkBusy ? "Creating…" : <>Request card hold for <Money amount={data.deposit_amount} /></>}
                  </button>
                )}
                <div className="text-xs text-neutral-500">Funds are only authorized, not charged — capture manually from Stripe if needed.</div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
